import React, { useState, useEffect, useRef } from "react";
import { authedFetch } from "../../services/api";
import { IonSpinner, IonIcon, IonModal, IonButton } from "@ionic/react";
import {
  imageOutline,
  closeOutline,
  addOutline,
  removeOutline,
  chevronForward,
  chevronBack,
} from "ionicons/icons";

interface AuthenticatedImageProps {
  src?: string;
  alt?: string;
  className?: string;
  placeholder?: React.ReactNode;
  onError?: () => void;
  onLoad?: () => void;
  images?: string[]; // Array of image URLs
  index?: number; // Index of the image in the array
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt,
  className = "",
  placeholder,
  onError,
  onLoad,
  images,
  index,
}) => {
  const [imageSrc, setImageSrc] = useState<string>("");
  const [imageBlobUrls, setImageBlobUrls] = useState<string[]>([]); // Store blob URLs for all images
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(index ?? 0);
  const [fullscreenLoading, setFullscreenLoading] = useState(false);

  const [layoutClasses, setLayoutClasses] = useState<string[]>([]);
  const [allImages, setAllImages] = useState<string[]>([]);
  // Pinch zoom state
  const pinchRef = useRef({
    isPinching: false,
    initialDistance: 0,
    initialScale: 1,
    initialPosition: { x: 0, y: 0 },
    centerPoint: { x: 0, y: 0 },
  });

  // Swipe navigation state
  const swipeRef = useRef({
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    isSwiping: false,
    isHorizontalSwipe: false,
  });

  // Fetch single image (for thumbnail view)
  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;
    let blobUrls: string[] = [];

    const fetchSingleImage = async () => {
      try {
        setLoading(true);
        setError(false);

        // No Content-Type — GET has no body, and setting a non-safelisted value
        // (e.g. image/*) forces a CORS preflight that the image route doesn't handle.
        const headers: Record<string, string> = {};

        // Auth + a single refresh-and-retry now come from authedFetch (see api.ts).
        // These three sites previously had no refresh path at all, which breaks on a
        // 15-minute Cognito access token.

        const response = await authedFetch(src as string, {
          method: "GET",
          headers,
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setImageSrc(objectUrl);
          setLoading(false);
          onLoad?.();
        }
      } catch (err) {
        console.error("Error loading authenticated image:", err);
        if (isMounted) {
          setError(true);
          setLoading(false);
          onError?.();
        }
      }
    };

    const fetchImagesFromArray = async () => {
      try {
        setLoading(true);
        setError(false);

        const headers: Record<string, string> = {};

        // Auth + a single refresh-and-retry now come from authedFetch (see api.ts).
        // These three sites previously had no refresh path at all, which breaks on a
        // 15-minute Cognito access token.

        const fetches =
          images?.map(async (imgUrl) => {
            const response = await authedFetch(imgUrl, {
              method: "GET",
              headers,
                });
            if (!response.ok)
              throw new Error(`Failed to fetch image: ${response.status}`);
            const blob = await response.blob();
            return URL.createObjectURL(blob);
          }) ?? [];

        blobUrls = await Promise.all(fetches);

        if (isMounted) {
          setImageBlobUrls(blobUrls);
          // If no single src was provided, use the first image as the thumbnail src
          if (!src) {
            setAllImages(blobUrls || []);
          }
          setLoading(false);
          onLoad?.();
        }
      } catch (err) {
        console.error("Error loading authenticated images:", err);
        if (isMounted) {
          setImageBlobUrls([]);
          setError(true);
          setLoading(false);
          onError?.();
        }
      }
    };

    if (src) {
      fetchSingleImage();
    } else if (images && images.length > 0) {
      // No src provided — fetch all images from the images array and store blob URLs
      fetchImagesFromArray();
    } else {
      // Nothing to load
      setLoading(false);
    }

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [src, images, onError, onLoad]);

  // Fetch all images for fullscreen mode (with authentication)
  useEffect(() => {
    let isMounted = true;
    let blobUrls: string[] = [];

    const fetchAllImages = async () => {
      if (!images || images.length === 0) return;
      try {
        setFullscreenLoading(true);

        const headers: Record<string, string> = {};

        // Auth + a single refresh-and-retry now come from authedFetch (see api.ts).
        // These three sites previously had no refresh path at all, which breaks on a
        // 15-minute Cognito access token.

        const fetches = images.map(async (imgUrl) => {
          const response = await authedFetch(imgUrl, {
            method: "GET",
            headers,
            });
          if (!response.ok)
            throw new Error(`Failed to fetch image: ${response.status}`);
          setFullscreenLoading(false);
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        });

        blobUrls = await Promise.all(fetches);

        if (isMounted) {
          setFullscreenLoading(false);
          setImageBlobUrls(blobUrls);
        }
      } catch (err) {
        console.error("Error loading authenticated images:", err);
        if (isMounted) {
          setImageBlobUrls([]);
          setFullscreenLoading(false);
        }
      }
    };

    if (isFullscreen && images && images.length > 0) {
      fetchAllImages();
    }

    return () => {
      isMounted = false;
      // Cleanup blob URLs
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
      setImageBlobUrls([]);
      setFullscreenLoading(false);
    };
  }, [isFullscreen, images]);
  useEffect(() => {
    const MAX_TILES = 4;
    if (!images || images.length === 0) {
      setLayoutClasses([]);
      return;
    }

    const count = images.length;
    let classes: string[] = [];

    // Grid is treated as 4 virtual columns (so col-span-2 == 50% width)
    // row-span controls how many auto-rows (200px) the item occupies.
    switch (Math.min(count, MAX_TILES)) {
      case 1:
        // Single image: full width, taller (span 2 rows)
        classes = ["col-span-1 row-span-1"];
        break;
      case 2:
        // Two images: side-by-side, each takes half width and full height
        classes = ["col-span-1 row-span-1", "col-span-1 row-span-1"];
        break;
      case 3:
        // Three images:
        classes = [
          "col-span-1 row-span-1",
          "col-span-1 row-span-1",
          "col-span-1 row-span-1",
        ];
        break;
      default:
        // 4 or more: 2x2 grid quadrants (each takes half width and one row)
        classes = [
          "col-span-2 row-span-1 aspect-square",
          "col-span-2 row-span-1 aspect-square",
          "col-span-2 row-span-1 aspect-square",
          "col-span-2 row-span-1 aspect-square",
        ];
        break;
    }

    // Ensure we never set more than MAX_TILES classes
    setLayoutClasses(classes.slice(0, MAX_TILES));
  }, [images]);
  const handleImageClick = (imageIndex: number) => {
    setIsFullscreen(true);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setCurrentIndex(imageIndex);
  };

  const handleNext = () => {
    if (images && images.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handlePrev = () => {
    if (images && images.length > 0) {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Clamp position so image stays within container bounds
  useEffect(() => {
    if (!imageRef.current) return;
    const container = imageRef.current;
    const img = container.querySelector("img");
    if (!img) return;

    const containerRect = container.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    // Only clamp if zoomed in
    if (scale > 1) {
      const maxX = Math.max(0, (imgRect.width - containerRect.width) / 2);
      const maxY = Math.max(0, (imgRect.height - containerRect.height) / 2);

      setPosition((pos) => ({
        x: Math.max(-maxX, Math.min(pos.x, maxX)),
        y: Math.max(-maxY, Math.min(pos.y, maxY)),
      }));
    } else {
      // Reset position if not zoomed
      if (position.x !== 0 || position.y !== 0) {
        setPosition({ x: 0, y: 0 });
      }
    }
  }, [scale, position.x, position.y, isFullscreen, currentIndex]);

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.5, 0.25));
  };

  // Calculate distance between two touch points
  const getTouchDistance = (touch1: any, touch2: any) => {
    return Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY,
    );
  };

  // Calculate center point between two touches
  const getTouchCenter = (touch1: any, touch2: any) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  // Smooth pinch zoom handler
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch zoom starting
      e.preventDefault();
      pinchRef.current.isPinching = true;

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      pinchRef.current.initialDistance = getTouchDistance(touch1, touch2);
      pinchRef.current.initialScale = scale;
      pinchRef.current.initialPosition = { ...position };
      pinchRef.current.centerPoint = getTouchCenter(touch1, touch2);

      // Reset swipe state when pinching starts
      swipeRef.current.isSwiping = false;
    } else if (e.touches.length === 1 && !pinchRef.current.isPinching) {
      // Single touch for dragging or swiping
      const touch = e.touches[0];

      swipeRef.current.startX = touch.clientX;
      swipeRef.current.startY = touch.clientY;
      swipeRef.current.currentX = touch.clientX;
      swipeRef.current.currentY = touch.clientY;

      if (scale > 1) {
        // Dragging mode when zoomed in
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - position.x,
          y: touch.clientY - position.y,
        });
      } else {
        // Swiping mode when not zoomed
        swipeRef.current.isSwiping = true;
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current.isPinching) {
      // Pinch zoom in progress
      e.preventDefault();

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      const currentDistance = getTouchDistance(touch1, touch2);
      const currentCenter = getTouchCenter(touch1, touch2);

      // Calculate new scale
      const scaleChange = currentDistance / pinchRef.current.initialDistance;
      const newScale = Math.min(
        Math.max(pinchRef.current.initialScale * scaleChange, 0.25),
        4,
      );

      // Calculate position offset to zoom toward the pinch center
      const rect = imageRef.current?.getBoundingClientRect();
      if (rect) {
        const centerX = pinchRef.current.centerPoint.x - rect.left;
        const centerY = pinchRef.current.centerPoint.y - rect.top;

        // Calculate the difference in position due to zoom
        const scaleDiff = newScale - pinchRef.current.initialScale;
        const offsetX = (centerX - rect.width / 2) * scaleDiff;
        const offsetY = (centerY - rect.height / 2) * scaleDiff;

        setPosition({
          x: pinchRef.current.initialPosition.x - offsetX,
          y: pinchRef.current.initialPosition.y - offsetY,
        });
      }

      setScale(newScale);
    } else if (e.touches.length === 1 && !pinchRef.current.isPinching) {
      const touch = e.touches[0];

      if (isDragging && scale > 1) {
        // Dragging when zoomed in
        e.preventDefault();
        setPosition({
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y,
        });
      } else if (swipeRef.current.isSwiping && scale === 1) {
        // Swiping for navigation
        swipeRef.current.currentX = touch.clientX;
        swipeRef.current.currentY = touch.clientY;

        const deltaX = Math.abs(touch.clientX - swipeRef.current.startX);
        const deltaY = Math.abs(touch.clientY - swipeRef.current.startY);

        // Determine if it's a horizontal swipe
        if (deltaX > 10 || deltaY > 10) {
          swipeRef.current.isHorizontalSwipe = deltaX > deltaY;
        }
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (pinchRef.current.isPinching) {
      // End of pinch zoom
      pinchRef.current.isPinching = false;
    } else if (swipeRef.current.isSwiping && scale === 1) {
      // End of swipe
      const deltaX = swipeRef.current.currentX - swipeRef.current.startX;
      const deltaY = Math.abs(
        swipeRef.current.currentY - swipeRef.current.startY,
      );

      // Only trigger navigation if it's a horizontal swipe
      if (
        swipeRef.current.isHorizontalSwipe &&
        Math.abs(deltaX) > 50 &&
        deltaY < 100
      ) {
        if (deltaX > 0) {
          handlePrev();
        } else {
          handleNext();
        }
      }

      swipeRef.current.isSwiping = false;
      swipeRef.current.isHorizontalSwipe = false;
    }

    setIsDragging(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.min(Math.max(scale + delta, 0.25), 4);

    if (newScale !== scale) {
      setScale(newScale);

      // If zooming out to original size or below, reset position
      if (newScale <= 1) {
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  if (loading) {
    return (
      <div
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
        }}
      >
        {placeholder || <IonSpinner name="crescent" />}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={className}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
          color: "#999",
        }}
      >
        <IonIcon
          icon={imageOutline}
          style={{ fontSize: "48px", marginBottom: "8px" }}
        />
        {/* <span>Failed to load image</span> */}
      </div>
    );
  }

  return (
    <>
      {src ? (
        <img
          src={imageSrc}
          alt={alt}
          className={className}
          // onClick={() => handleImageClick(index ?? 0)}
          style={{
            cursor: "pointer",
            display: "block",
            width: "100%",
            height: "auto",
            objectFit: "contain",
          }}
        />
      ) : (
        <div
          className={`grid grid-cols-${allImages.length > 4 ? 4 : allImages.length
            } auto-rows-auto gap-1 bg-white p-0.5 rounded-md w-full max-w-2xl mx-auto`}
          style={{
            cursor: "pointer",
            minHeight: "200px",
            maxHeight: allImages.length < 4 ? "400px" : "auto",
          }}
        >
          {layoutClasses.map((className, i) => {
            const imageIndex = i;
            const isOverlayTile =
              layoutClasses.length === 4 && i === 3 && allImages.length > 4;

            return (
              <div
                key={i}
                className={`relative overflow-hidden shadow-sm cursor-pointer ${className}`}
                onClick={() => {
                  handleImageClick(imageIndex);
                }}
              >
                <img
                  src={allImages[imageIndex]}
                  alt={`post image ${imageIndex + 1}`}
                  className={`w-full h-full object-cover transition-all duration-300 max-h-[400px] sm:max-h-[400px] max-[600px]:max-h-[340px] ${isOverlayTile ? "opacity-50" : "hover:scale-105"
                    }`}
                />

                {/* Render the +X overlay on top of the image if it's the designated tile */}
                {isOverlayTile && (
                  <div
                    className="absolute inset-0 z-1 flex items-center justify-center"
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      width: "100%",
                      height: "100%",
                      aspectRatio: "1 / 1",
                    }}
                  >
                    <span className="text-white text-3xl font-bold pointer-events-none">
                      +{allImages.length - 4}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Fullscreen Modal */}
      <IonModal
        isOpen={isFullscreen}
        onDidDismiss={handleCloseFullscreen}
        className="fullscreen-image-modal transparent-modal-bg"
        style={
          {
            // Force fullscreen on all devices including tablets/iPads
            "--width": "100vw",
            "--height": "100vh",
            "--max-width": "100vw",
            "--max-height": "100vh",
            "--min-width": "100vw",
            "--min-height": "100vh",
            // Override Ionic's default modal positioning
            "--top": "0",
            "--left": "0",
            "--right": "0",
            "--bottom": "0",
            // Remove any default margins/padding
            "--margin": "0",
            "--padding": "0",
            // Ensure full viewport coverage
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            maxWidth: "100vw",
            maxHeight: "100vh",
            margin: 0,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // preserve visual styles
            "--background": "rgba(255,255,255,0.2)",
            "--backdrop-filter": "blur(16px)",
            // Remove border radius for true fullscreen
            "--border-radius": "0",
            borderRadius: 0,
          } as React.CSSProperties
        }
      >
        {/* Blurred background overlay */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            background: "rgba(255,255,255,0.35)",
          }}
        />
        <div
          style={{
            width: "95%",
            height: "90%",
            backgroundColor: "transparent",
            position: "relative",
            overflow: "hidden",
            margin: "auto",
            borderRadius: "12px",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "18px",
              right: "25px",
              zIndex: 10010,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "20%",
              background: "rgba(0,0,0,0.5)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            {/* Close Button */}
            <IonButton
              fill="clear"
              onClick={handleCloseFullscreen}
              style={{
                color: "white",
                fontSize: "28px",
                width: "100vw",
                height: "100vh",
                margin: 0,
                padding: 0,
                borderRadius: "50%",
              }}
            >
              <IonIcon icon={closeOutline} slot="icon-only" />
            </IonButton>
          </div>

          {/* Left Arrow */}
          {images && images.length > 1 && (
            <IonButton
              fill="clear"
              onClick={handlePrev}
              style={{
                position: "absolute",
                top: "50%",
                left: "10px",
                transform: "translateY(-50%)",
                color: "white",
                zIndex: 10010,
                borderRadius: "50%",
              }}
            >
              <IonIcon
                icon={chevronBack}
                style={{
                  fontSize: "40px",
                  color: "#FFFFFF",
                  flexShrink: 0,
                }}
              />
            </IonButton>
          )}

          {/* Right Arrow */}
          {images && images.length > 1 && (
            <IonButton
              fill="clear"
              onClick={handleNext}
              style={{
                position: "absolute",
                top: "50%",
                right: "10px",
                transform: "translateY(-50%)",
                color: "white",
                zIndex: 10010,
                borderRadius: "50%",
              }}
            >
              <IonIcon
                icon={chevronForward}
                style={{
                  fontSize: "40px",
                  color: "#FFFFFF",
                  flexShrink: 0,
                }}
              />
            </IonButton>
          )}

          {/* Zoom Controls */}
          <div
            style={{
              position: "absolute",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "10px",
              zIndex: 10010,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              borderRadius: "25px",
              padding: "5px",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "10px" }}>
              <IonButton
                fill="clear"
                onClick={handleZoomOut}
                style={{ color: "white" }}
              >
                <IonIcon icon={removeOutline} slot="icon-only" />
              </IonButton>
              <div
                style={{
                  color: "white",
                  padding: "8px 15px",
                  minWidth: "60px",
                  textAlign: "center",
                }}
              >
                {Math.round(scale * 100)}%
              </div>
              <IonButton
                fill="clear"
                onClick={handleZoomIn}
                style={{ color: "white" }}
              >
                <IonIcon icon={addOutline} slot="icon-only" />
              </IonButton>
              {/* Reset Button */}
              <IonButton
                fill="clear"
                onClick={handleReset}
                style={{ color: "white" }}
                disabled={scale === 1 && position.x === 0 && position.y === 0}
              >
                <span style={{ fontSize: "16px", fontWeight: 500 }}>Reset</span>
              </IonButton>
            </div>
            {/* Image index display */}
            {images && images.length > 1 && (
              <div
                style={{
                  color: "white",
                  fontSize: "15px",
                  marginTop: "2px",
                  letterSpacing: "0.5px",
                  fontWeight: 500,
                }}
              >
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Image Container */}
          <div
            ref={imageRef}
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              backgroundColor: "transparent",
              touchAction: "none",
              cursor:
                scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              position: "relative",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {(fullscreenLoading ||
              (images &&
                images.length > 1 &&
                !imageBlobUrls[currentIndex])) && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.6)",
                    zIndex: 10020,
                    borderRadius: "8px",
                  }}
                >
                  <IonSpinner name="crescent" />
                </div>
              )}
            {!fullscreenLoading &&
              (!images ||
                images.length <= 1 ||
                imageBlobUrls[currentIndex]) && (
                <img
                  src={
                    images && images.length >= 1
                      ? imageBlobUrls[currentIndex] || ""
                      : imageSrc
                  }
                  alt={alt}
                  style={{
                    width: scale > 1 ? "auto" : "100%",
                    height: scale > 1 ? "auto" : "100%",
                    maxWidth: scale > 1 ? "none" : "100%",
                    maxHeight: scale > 1 ? "none" : "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transition:
                      isDragging || pinchRef.current.isPinching
                        ? "none"
                        : "transform 0.2s ease-out",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                  draggable={false}
                />
              )}
          </div>
        </div>
      </IonModal>
    </>
  );
};

export default AuthenticatedImage;
